// Lightweight error reporting via Microsoft Clarity (already loaded in index.html).
// Clarity sessions containing these events can be found by filtering on the
// "js_error" / "react_crash" custom events, and the error details show up as
// custom tags on the session.

const callClarity = (...args) => {
  try {
    if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
      window.clarity(...args);
    }
  } catch (e) {
    // Never let the reporter itself throw
  }
};

export const reportError = (error, context = {}) => {
  const message = error?.message || String(error);
  const stack = error?.stack || '';

  // eslint-disable-next-line no-console
  console.error('[errorReporting]', context.source || 'error', error);

  // Tags are limited in length; keep the most useful part.
  callClarity('set', 'error_message', message.slice(0, 255));
  if (stack) {
    callClarity('set', 'error_stack', stack.slice(0, 255));
  }
  if (context.source) {
    callClarity('set', 'error_source', String(context.source).slice(0, 255));
  }
  try {
    callClarity('set', 'error_route', String(window.location.pathname).slice(0, 255));
  } catch (e) {
    // no window / location in some test environments
  }
  if (context.componentStack) {
    callClarity('set', 'error_component_stack', String(context.componentStack).trim().slice(0, 255));
  }

  callClarity('event', context.source === 'react_error_boundary' ? 'react_crash' : 'js_error');
  // Ask Clarity to prioritize recording this session so we get the replay.
  callClarity('upgrade', context.source || 'js_error');
};

// Noise injected by browser extensions (wallets, password managers, etc.) and
// well-known benign browser warnings. These are not our bugs, so we neither
// report them to Clarity nor let them trip the dev error overlay.
const EXTENSION_SCHEME = /(chrome|moz|safari(-web)?|edge)-extension:\/\//i;
const IGNORED_ERROR_PATTERNS = [
  /No Listener:/i,              // webext-bridge style extension messaging
  /tabs:(outgoing|incoming)/i,  // e.g. "tabs:outgoing.message.ready"
  /message (channel|port) closed/i,
  /Extension context invalidated/i,
  /ResizeObserver loop/i,       // benign browser warning
];

const isIgnorableError = (error, event) => {
  const message = error?.message || (typeof error === 'string' ? error : '') || '';
  const stack = error?.stack || '';
  const filename = event?.filename || '';
  if (EXTENSION_SCHEME.test(filename) || EXTENSION_SCHEME.test(stack)) return true;
  return IGNORED_ERROR_PATTERNS.some((re) => re.test(message) || re.test(stack));
};

let initialized = false;

export const initGlobalErrorCapture = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  // Capture phase so we run as early as possible; stopImmediatePropagation on
  // ignorable errors best-effort prevents the dev-server overlay from showing.
  window.addEventListener('error', (event) => {
    if (isIgnorableError(event.error || event.message, event)) {
      event.stopImmediatePropagation();
      return;
    }
    reportError(event.error || event.message, { source: 'window_onerror' });
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    if (isIgnorableError(event.reason)) {
      event.stopImmediatePropagation();
      return;
    }
    reportError(event.reason, { source: 'unhandled_rejection' });
  }, true);
};
